import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MiqBehaviors } from '@shared/behaviors';
import { TextInputConfig } from '@shared/models/icommons.model';
import { simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import { isMasked } from '@shared/utils/helpers/sanitizers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { TextInput } from '../text-input';

@UntilDestroy()
@Component({
  selector: 'miq-text',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
  providers: [{ provide: MiqBehaviors, useExisting: TextComponent }],
  standalone: false,
})
export class TextComponent extends TextInput implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder = '';
  @Input() defaultEmptyValue = ''; // TODO: Change to null when null values become a valid value on EDP
  @Input() autocomplete: boolean;
  @Input() customConfigs: TextInputConfig = {};

  @Output() currentValueChange = new EventEmitter<string>();
  @Output() clear = new EventEmitter<string>();

  autocompleteValue = 'no-autofill';

  customSearchConfig: { isEnabled: boolean };

  get clearButtonEnabled(): boolean {
    return this.customClearButtonConfig.isEnabled && !!this.customClearButtonConfig.currentValue;
  }

  get customValue(): string {
    return this.clearButtonEnabled ? this.customClearButtonConfig.currentValue : null;
  }

  get customClearButtonConfig(): { isEnabled: boolean; restrictTyping?: boolean; currentValue?: string } {
    return (this.customConfigs && this.customConfigs.clearButton) || { isEnabled: false };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.autocompleteValue = this.autocomplete ? 'on' : 'no-autofill';
    this.customSearchConfig = this.customConfigs && this.customConfigs.search;
    const isSearchInput = this.customSearchConfig && this.customSearchConfig.isEnabled;
    if (isSearchInput) {
      this.fc.valueChanges.pipe(debounceTime(500), untilDestroyed(this)).subscribe(() => {
        this.onChange(this.clearButtonEnabled ? this.customClearButtonConfig.currentValue : null);
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (!!changes.currentValue && !changes.currentValue.firstChange) {
      this.fc.setValue(this.currentValue);
    }
  }

  ngOnDestroy(): void {
    // required by untilDestroyed
  }

  public blurChange() {
    // prevent disabling by typing 5 stars
    if (isMasked(this.fc.value)) {
      this.fc.setValue(this.defaultEmptyValue);
    }
    this.onChange(this.clearButtonEnabled ? this.customClearButtonConfig.currentValue : null);
  }

  public onChange(customValue?: string) {
    const newValue = customValue || this.fc.value;
    this.currentValue = simpleValueValidation(newValue) ? newValue : this.defaultEmptyValue;
    let value = this.currentValue;
    if (this.currentValue != null) {
      value = this.valueSetter ? this.valueSetter(this.currentValue) : this.currentValue.trim();
    }
    this.currentValueChange.emit(value);
  }

  public clearValue() {
    this.clear.emit(this.currentValue);
    this.fc.setValue(this.defaultEmptyValue);
  }
}
